<template>
  <div class="relations-section">
    <h3>{{ t('customData.relationEditor.title') }}</h3>

    <div class="datatable-container">
      <!-- Manual table to allow interleaving override sections -->
      <div class="relations-table">
        <!-- Header -->
        <div class="table-header">
          <div class="th col-local-field">
            {{ t('customData.relationEditor.localFieldHeader') }}
            <i
              v-tooltip="t('customData.relationEditor.localFieldTooltip')"
              class="pi pi-info-circle ml-tooltip"
            />
          </div>
          <div class="th col-target-type">
            {{ t('customData.relationEditor.targetTypeHeader') }}
            <i
              v-tooltip="t('customData.relationEditor.targetTypeTooltip')"
              class="pi pi-info-circle ml-tooltip"
            />
          </div>
          <div class="th col-target-field">
            {{ t('customData.relationEditor.targetFieldHeader') }}
            <i
              v-tooltip="t('customData.relationEditor.targetFieldTooltip')"
              class="pi pi-info-circle ml-tooltip"
            />
          </div>
          <div class="th col-actions" />
        </div>

        <!-- Empty state -->
        <div
          v-if="relationships.length === 0"
          class="empty-state"
        >
          <i class="fas fa-project-diagram empty-state-icon" />
          <p>{{ t('customData.relationEditor.emptyMessage') }}</p>
        </div>

        <!-- Relation groups (relation row + optional overrides) -->
        <template
          v-for="(relation, rIndex) in relationships"
          :key="relation.id"
        >
          <div
            class="relation-group"
            :class="{ 'relation-group--with-overrides': isRelationOverridable(relation) }"
          >
            <!-- EDIT MODE ROW -->
            <div
              v-if="relationshipOnEdit(relation)"
              class="table-row row-is-editing"
            >
              <div class="td col-local-field">
                <BaseDropdown
                  :model-value="relation.localField"
                  :options="eligibleLocalFields(relation).map(f => ({ value: f.name, label: f.name }))"
                  :placeholder="t('customData.relationEditor.localFieldPlaceholder')"
                  class="w-full"
                  @update:model-value="newValue => { onLocalFieldChange(relation, newValue); }"
                />
              </div>
              <div class="td col-target-type">
                <BaseDropdownMultiple
                  :model-value="relation.selectedTargetSchema"
                  :options="targetSchemaOptions"
                  option-label="label"
                  option-value="value"
                  option-group-label="groupLabel"
                  option-group-children="items"
                  :placeholder="t('customData.relationEditor.targetTypePlaceholder')"
                  class="w-full"
                  @change="() => onTargetSchemaChange(relation)"
                  @update:model-value="newValue => { relation.selectedTargetSchema = newValue; }"
                />
              </div>
              <div class="td col-target-field">
                <template v-if="hasMultipleTargetFields(relation)">
                  <BaseDropdown
                    :model-value="relation.field"
                    :options="availableTargetFields(relation).map(f => ({ value: f.name, label: f.name }))"
                    :placeholder="t('customData.relationEditor.targetFieldPlaceholder')"
                    class="w-full"
                    @update:model-value="newValue => { relation.field = newValue; }"
                  />
                </template>
                <template v-else>
                  <span
                    v-if="relation.field"
                    class="target-field-readonly"
                  >{{ relation.field }}</span>
                  <span
                    v-else
                    class="target-field-empty"
                  >—</span>
                </template>
              </div>
              <div class="td col-actions">
                <div class="actions-cell">
                  <i
                    v-tooltip="t('customData.relationEditor.saveRelationTooltip')"
                    class="fas fa-check action-icon"
                    @click="saveRelation(relation.id)"
                  />
                  <i
                    v-if="!isNewAddRelationship(relation)"
                    v-tooltip="t('customData.relationEditor.cancelEditTooltip')"
                    class="fas fa-times action-icon"
                    @click="cancelEditRelation(relation.id)"
                  />
                  <i
                    v-tooltip="t('customData.relationEditor.deleteRelationTooltip')"
                    class="far fa-trash-alt action-icon"
                    @click="removeRelation(relation.id)"
                  />
                </div>
              </div>
            </div>

            <!-- READ MODE ROW -->
            <div
              v-else
              class="table-row"
            >
              <div class="td col-local-field">
                <div class="field-display-inline">
                  <i class="fas fa-database field-icon field-icon--local" />
                  <span class="field-name field-name--local">{{ relation.localField }}</span>
                </div>
              </div>
              <div class="td col-target-type">
                <div class="schema-display">
                  <span
                    class="schema-type-badge"
                    :class="relation.schema_type === 'system' ? 'schema-type-badge--system' : 'schema-type-badge--custom'"
                  >
                    {{ relation.schema_type === 'system' ? t('customData.relationEditor.systemSchemaLabel') : t('customData.relationEditor.customSchemaLabel') }}
                  </span>
                  <span class="schema-name">{{ getTargetSchemaLabel(relation) }}</span>
                </div>
              </div>
              <div class="td col-target-field">
                <div class="field-display-inline">
                  <i class="fas fa-link field-icon field-icon--target" />
                  <span class="field-name field-name--target">{{ getTargetFieldLabel(relation) }}</span>
                  <i
                    v-if="getTargetFieldTooltip(relation)"
                    v-tooltip="getTargetFieldTooltip(relation)"
                    class="pi pi-info-circle target-field-info"
                  />
                </div>
              </div>
              <div class="td col-actions">
                <div
                  v-if="isRelationshipEditable(relation)"
                  class="actions-cell"
                >
                  <i
                    v-tooltip="t('customData.relationEditor.editRelationTooltip')"
                    class="fas fa-pencil-alt action-icon"
                    @click="editRelation(relation.id)"
                  />
                  <i
                    v-tooltip="t('customData.relationEditor.deleteRelationTooltip')"
                    class="far fa-trash-alt action-icon"
                    @click="removeRelation(relation.id)"
                  />
                </div>
              </div>
            </div>

            <!-- OVERRIDE SECTION (right below associated relation, inside the group) -->
            <div
              v-if="isRelationOverridable(relation)"
              class="overrides-inline-section"
            >
              <div class="overrides-inline-header">
                <i class="fas fa-exchange-alt" />
                <span>{{ t('customData.overrideEditor.sectionTitle') }} — {{ getTargetSchemaLabel(relation) }}</span>
              </div>
              <SchemaOverridesEditor
                :id-relation="relation.id"
              />
            </div>
          </div>

          <!-- Visual separator between relation groups -->
          <div
            v-if="rIndex < relationships.length - 1"
            class="relation-group-separator"
          />
        </template>
      </div>
    </div>

    <!-- Add relation button (always right after the table) -->
    <div class="add-relation-container">
      <div
        class="add-relation-item"
        @click="addNewEmptyRelation"
      >
        <span class="add-relation-icon-circle">
          <i class="fas fa-plus" />
        </span>
        <span>{{ t('customData.relationEditor.addRelationButton') }}</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent, computed,
} from 'vue';
import { useI18n } from 'vue-i18n';
import BaseDropdown from '@/components/fields/partials/BaseDropdown.vue';
import BaseDropdownMultiple from '@/components/fields/partials/BaseDropdownMultiple.vue';
import Tooltip from 'primevue/tooltip';
import { OVERRIDABLE_FIELDS } from '@/composables/custom-data/overridableSystemFields';
import {
  Field, SystemSchema, ForeignKeyRow,
  SystemSchemaField,
} from '@/composables/custom-data/customData.interfaces';
import { useStore } from '@/store';
import { showToastError } from '@/helpers';
import SchemaOverridesEditor from './SchemaOverridesEditor.vue';

export default defineComponent({
  name: 'SchemaRelationsEditor',

  components: {
    BaseDropdownMultiple,
    BaseDropdown,
    SchemaOverridesEditor,
  },

  directives: {
    tooltip: Tooltip,
  },

  setup() {
    const { t } = useI18n();
    const store = useStore();
    const idCustomDataDefinition = computed(() => store.getters['customObject/getIdCustomDataDefinition']);
    const fields = computed(() => store.getters['customObject/getFields']);
    const relationships = computed(() => store.getters['customObject/getRelationships']);
    const currentEditingElement = computed(() => store.getters['customObject/getCurrentEditingElement']);
    const neededData = computed(() => store.getters['customObject/getNeededData']);
    const existingRelationships = computed(() => store.getters['customObject/getNeededDataExistingRelationships'] || []);
    const overrides = computed(() => store.getters['customObject/getOverrides'] || []);

    const isRelationshipEditable = (relationship: ForeignKeyRow): boolean => !existingRelationships.value.map((r: any) => r.id).includes(relationship.id);

    const relationshipOnEdit = (relationship: ForeignKeyRow): boolean => currentEditingElement.value
     && currentEditingElement.value.id === relationship.id
      && currentEditingElement.value.elementType === 'relation';

    const isNewAddRelationship = (relationship: ForeignKeyRow): boolean => currentEditingElement.value
     && currentEditingElement.value.id === relationship.id
      && currentEditingElement.value.elementType === 'relation'
       && currentEditingElement.value.isNew;

    const addNewEmptyRelation = () => {
      if (currentEditingElement.value !== null) {
        showToastError(t('customData.relationEditor.actions.addEmptyRelation'), 4000);
        return;
      }
      store.dispatch('customObject/handleRelationAction', { action: 'add-new-empty' });
    };

    const removeRelation = (id: number) => store.dispatch('customObject/handleRelationAction', { action: 'remove', id });

    const editRelation = (id: number) => {
      if (currentEditingElement.value !== null) {
        showToastError(t('customData.relationEditor.actions.editRelation'), 4000);
        return;
      }
      store.dispatch('customObject/handleRelationAction', { action: 'edit', id });
    };

    const cancelEditRelation = (id: number) => store.dispatch('customObject/handleRelationAction', { action: 'cancel-edit', id });

    const saveRelation = (id: number) => {
      const fkRow = relationships.value.find((fk: ForeignKeyRow) => fk.id === id);
      if (fkRow) {
        if (!fkRow.localField || !fkRow.schema || !fkRow.field) {
          showToastError(t('customData.relationEditor.actions.saveRelation'), 4000);
          return;
        }
        store.dispatch('customObject/handleRelationAction', { action: 'save', id });
      }
    };

    const findFieldIndexByName = (name: string): number => fields.value.findIndex((f: Field) => f.name === name);

    const getAllowedLocalFieldTypes = (fk: ForeignKeyRow): string[] => {
      if (fk.schema_type === 'custom' && fk.schema) {
        const schema = findCustomSchema(fk.schema);
        if (schema?.fields?.length > 0) {
          return [...new Set(schema.fields.map((f: SystemSchemaField) => f.type))] as string[];
        }
        return ['number'];
      }
      if (fk.schema_type === 'system' && fk.schema) {
        const schema = neededData.value.systemSchemas.find((s: SystemSchema) => s.name === fk.schema);
        if (schema?.fields?.length > 0) {
          return [...new Set(schema.fields.map((f: SystemSchemaField) => f.type))] as string[];
        }
      }
      return ['text', 'number'];
    };

    const eligibleLocalFields = (fkBeingEdited: ForeignKeyRow): Field[] => {
      const usedLocalFields = new Set(
        relationships.value
          .filter((fk: ForeignKeyRow) => fk.id !== fkBeingEdited.id && !!fk.localField)
          .map((fk: ForeignKeyRow) => fk.localField),
      );
      // Fields already used as custom_field_name in overrides must be excluded
      const usedInOverrides = new Set(
        overrides.value
          .filter((o: any) => !!o.custom_field_name)
          .map((o: any) => o.custom_field_name),
      );
      // Only filter by type if the target schema is already selected
      const allowedTypes = fkBeingEdited.schema
        ? getAllowedLocalFieldTypes(fkBeingEdited)
        : null;
      return fields.value.filter((field: Field) => {
        // Type check: text or number fields only; restrict to target-compatible types if target is known
        if (field.type !== 'text' && field.type !== 'number') return false;
        if (allowedTypes && !allowedTypes.includes(field.type)) return false;
        if (usedInOverrides.has(field.name)) return false;
        return !usedLocalFields.has(field.name) || field.name === fkBeingEdited.localField;
      });
    };

    const targetSchemaOptions = computed((): any[] => {
      const usedTargetSchemas = new Set(
        relationships.value
          .filter((fk: ForeignKeyRow) => !relationshipOnEdit(fk) && fk.schema)
          .map((fk: ForeignKeyRow) => `${fk.schema_type}::${fk.schema}`),
      );

      const options: any[] = [];
      if (neededData.value.systemSchemas.length > 0) {
        const systemItems = neededData.value.systemSchemas
          .filter((s: any) => !usedTargetSchemas.has(`system::${s.name}`))
          .map((s: any) => ({ label: s.label, value: { type: 'system' as const, name: s.name } }));
        if (systemItems.length > 0) {
          options.push({ groupLabel: t('customData.relationEditor.systemSchemaLabel'), items: systemItems });
        }
      }
      if (neededData.value.customSchemas.length > 0) {
        let customItems = neededData.value.customSchemas
          .filter((s: any) => !usedTargetSchemas.has(`custom::${s.name}`));
        if (idCustomDataDefinition.value > 0) {
          customItems = customItems.filter((s: any) => parseInt(s.id_custom_data_definition, 10) !== parseInt(idCustomDataDefinition.value.toString(), 10));
        }
        customItems = customItems.map((s: any) => ({ label: s.label, value: { type: 'custom' as const, name: s.name } }));
        if (customItems.length > 0) {
          options.push({ groupLabel: t('customData.relationEditor.customSchemaLabel'), items: customItems });
        }
      }
      return options;
    });

    // Helper: find a custom schema by table_name OR id_custom_data_definition
    const findCustomSchema = (schemaRef: string): any | undefined => neededData.value.customSchemas.find(
      (s: any) => s.name === schemaRef || String(s.id_custom_data_definition) === String(schemaRef),
    );

    // Noms de champs cibles distincts d'un schéma système (les fields sont
    // dupliqués par type — ex. product_id text + product_id number).
    const systemTargetFieldNames = (schemaName: string): string[] => {
      const schema = neededData.value.systemSchemas.find((s: SystemSchema) => s.name === schemaName);
      const names = (schema?.fields || []).map((f: SystemSchemaField) => String(f.name));
      return [...new Set<string>(names)];
    };

    const getFixedTargetField = (schemaType: string, schemaName: string): string | null => {
      if (schemaType === 'system') {
        const names = systemTargetFieldNames(schemaName);
        // Une seule cible → auto-assignée (comportement historique). Plusieurs
        // cibles (ex. contacts : id_contact / group_id / lang / country) →
        // l'utilisateur choisit, comme pour les schémas custom.
        if (names.length === 1) return names[0];
        return null;
      }
      if (schemaType === 'custom') {
        const schema = findCustomSchema(schemaName);
        // If custom schema has only 'id' (no single unique key), auto-assign
        if (!schema?.fields || schema.fields.length <= 1) return 'id';
        // Multiple target fields available → user must choose
        return null;
      }
      return null;
    };

    const hasMultipleTargetFields = (fk: ForeignKeyRow): boolean => {
      if (!fk.schema) return false;
      if (fk.schema_type === 'system') {
        return systemTargetFieldNames(fk.schema).length > 1;
      }
      const schema = findCustomSchema(fk.schema);
      return schema?.fields?.length > 1;
    };

    const onTargetSchemaChange = (fk: ForeignKeyRow) => {
      const mutableFk = relationships.value.find((item: ForeignKeyRow) => item.id === fk.id);
      if (!mutableFk) return;
      const selection = mutableFk.selectedTargetSchema;
      if (selection && selection.length > 0) {
        mutableFk.schema_type = selection[0].type;
        mutableFk.schema = selection[0].name;
        const fixedField = getFixedTargetField(selection[0].type, selection[0].name);
        mutableFk.field = fixedField || '';

        // Check if current localField is still compatible with the new target schema
        if (mutableFk.localField) {
          const localFieldObj = fields.value.find((f: Field) => f.name === mutableFk.localField);
          if (localFieldObj) {
            const allowedTypes = getAllowedLocalFieldTypes(mutableFk);
            if (!allowedTypes.includes(localFieldObj.type)) {
              // Type incompatible → reset localField so the user picks a compatible one
              console.warn(`[RelationsEditor] localField "${mutableFk.localField}" (type: ${localFieldObj.type}) incompatible with target schema (allowed types: ${allowedTypes.join(', ')}). Resetting localField.`);
              showToastError(t('customData.relationEditor.localFieldTypeIncompatible', { field: mutableFk.localField, type: localFieldObj.type, allowedTypes: allowedTypes.join(', ') }), 5000);
              mutableFk.localField = '';
            }
          }
        }
      } else {
        mutableFk.schema_type = 'system';
        mutableFk.schema = '';
        mutableFk.field = '';
      }
    };

    const onLocalFieldChange = (fk: ForeignKeyRow, newValue: string) => {
      const mutableFk = relationships.value.find((item: ForeignKeyRow) => item.id === fk.id);
      if (!mutableFk) return;
      // Guard: ignore null/undefined emitted by dropdown when options change (PrimeVue auto-clear)
      if (newValue === null || newValue === undefined) return;
      mutableFk.localField = newValue;
      // If target field is set but no longer compatible with the new local field type, reset it
      if (mutableFk.field && mutableFk.schema) {
        const compatible = availableTargetFields(mutableFk);
        if (!compatible.some((f) => f.name === mutableFk.field)) {
          // Auto-assign if only one option, otherwise clear for user to choose
          const fixedField = getFixedTargetField(mutableFk.schema_type, mutableFk.schema);
          mutableFk.field = fixedField || '';
        }
      }
    };

    const usedTargetFields = computed(
      () => relationships.value
        .filter((fk: ForeignKeyRow) => !relationshipOnEdit(fk) && fk.schema && fk.field)
        .map((fk: ForeignKeyRow) => `${fk.schema_type}::${fk.schema}::${fk.field}`),
    );

    const availableTargetFields = (fk: ForeignKeyRow): (SystemSchemaField & { disabled?: boolean })[] => {
      if (!fk.schema) return [];
      const targetSchema = fk.schema_type === 'system'
        ? neededData.value.systemSchemas.find((s: SystemSchema) => s.name === fk.schema)
        : findCustomSchema(fk.schema);
      if (!targetSchema?.fields) return [];

      // Filter target fields by local field type compatibility (if localField is selected)
      let potentialFields = targetSchema.fields;
      if (fk.localField) {
        const localFieldIndex = findFieldIndexByName(fk.localField);
        if (localFieldIndex !== -1) {
          const localFieldType = fields.value[localFieldIndex].type;
          potentialFields = targetSchema.fields.filter(
            (tf: SystemSchemaField) => tf.type === localFieldType,
          );
        }
      }

      return potentialFields.map((targetField: SystemSchemaField) => {
        const targetIdentifier = `${fk.schema_type}::${fk.schema}::${targetField.name}`;
        const isUsedByAnother = usedTargetFields.value.includes(targetIdentifier)
                         && !(fk.originalData?.schema === fk.schema && fk.originalData?.field === targetField.name);
        return { ...targetField, disabled: isUsedByAnother };
      });
    };

    const getTargetSchemaLabel = (relation: ForeignKeyRow): string => {
      if (!relation.schema) return '';
      const schemas = relation.schema_type === 'system' ? neededData.value.systemSchemas : neededData.value.customSchemas;
      if (relation.schema_type === 'system') {
        const targetSchema = schemas.find((s: SystemSchema) => s.name === relation.schema);
        return targetSchema?.label || relation.schema;
      }
      // Custom: relation.schema can be table_name OR id_custom_data_definition (from API)
      const targetSchema = findCustomSchema(relation.schema);
      if (targetSchema) {
        return `${targetSchema.label} #${targetSchema.id_custom_data_definition}`;
      }
      return relation.schema;
    };

    const getTargetFieldTooltip = (fk: ForeignKeyRow): string => {
      if (fk.schema_type === 'custom' && fk.field === 'id') {
        return t('customData.relationEditor.targetFieldTooltips.customId');
      }
      if (fk.schema_type === 'custom' && fk.field && fk.field !== 'id') {
        return t('customData.relationEditor.targetFieldTooltips.customUniqueKey', { field: fk.field });
      }
      if (fk.schema_type === 'system' && fk.field === 'product_id') {
        return t('customData.relationEditor.targetFieldTooltips.productId');
      }
      if (fk.schema_type === 'system' && fk.field === 'id_contact') {
        return t('customData.relationEditor.targetFieldTooltips.contactId');
      }
      return '';
    };

    const isRelationOverridable = (relation: ForeignKeyRow): boolean => relation.schema_type === 'system'
     && !!relation.schema
     && Object.keys(OVERRIDABLE_FIELDS).includes(relation.schema as string)
     && !relationshipOnEdit(relation);

    const getTargetFieldLabel = (fk: ForeignKeyRow): string => {
      if (!fk.schema || !fk.field) return fk.field || '';
      const schemas = fk.schema_type === 'system' ? neededData.value.systemSchemas : neededData.value.customSchemas;
      const targetSchema = schemas.find((s: SystemSchema) => s.name === fk.schema);
      if (targetSchema?.fields) {
        const targetField = targetSchema.fields.find((f: SystemSchemaField) => f.name === fk.field);
        return targetField?.label || fk.field;
      }
      if (fk.schema_type === 'custom' && fk.field === 'id') {
        return 'id';
      }
      return fk.field;
    };

    return {
      t,
      relationships,
      isRelationshipEditable,
      relationshipOnEdit,
      isNewAddRelationship,
      eligibleLocalFields,
      targetSchemaOptions,
      onTargetSchemaChange,
      onLocalFieldChange,
      availableTargetFields,
      hasMultipleTargetFields,
      addNewEmptyRelation,
      removeRelation,
      editRelation,
      saveRelation,
      cancelEditRelation,
      getTargetFieldLabel,
      getTargetFieldTooltip,
      getTargetSchemaLabel,
      isRelationOverridable,
    };
  },
});
</script>

<style lang="scss" scoped>
.relations-section {
  h3 {
    margin-top: 0;
    margin-bottom: 20px;
    font-size: 1.2em;
    color: #333;
  }
}

/* Container — same as SchemaFieldsEditor */
.datatable-container {
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  border: 1px solid #e9ecef;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
}

.relations-table {
  width: 100%;
}

/* Header row */
.table-header {
  display: grid;
  grid-template-columns: 25% 30% 25% 1fr;
  background-color: #ffffff;
  border-bottom: 1px solid #f0f0f0;

  .th {
    color: #888;
    font-weight: 600;
    font-size: 0.75em;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 0.6rem 0.75rem;
    display: flex;
    align-items: center;
  }
}

/* Relation group — wraps a relation row + optional overrides */
.relation-group {
  &--with-overrides {
    /* Subtle left accent to visually connect the override section to the relation */
    border-left: 3px solid transparent;
  }
}

/* Separator between relation groups */
.relation-group-separator {
  height: 8px;
  background: #f4f5f7;
  border-top: 1px solid #eee;
  border-bottom: 1px solid #eee;
}

/* Data rows */
.table-row {
  display: grid;
  grid-template-columns: 25% 30% 25% 1fr;
  background-color: #ffffff;
  transition: all 0.2s ease;
  align-items: center;

  &:hover {
    background-color: #f8f9fa;
  }

  /* Edit mode — green accent */
  &.row-is-editing {
    background-color: #f0f8e8 !important;
    border-left: 3px solid #94c840;

    &:hover {
      background-color: #f0f8e8 !important;
    }
  }

  .td {
    padding: 0.75rem;
    vertical-align: middle;
  }
}

/* Empty state */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2.5rem 1rem;
  color: #adb5bd;

  .empty-state-icon {
    font-size: 2rem;
    margin-bottom: 0.75rem;
    opacity: 0.5;
  }

  p {
    margin: 0;
    font-style: italic;
    font-size: 0.95em;
  }
}

/* Field display inline (read mode) */
.field-display-inline {
  display: flex;
  align-items: center;
  gap: 0.5rem;

  .field-icon {
    font-size: 0.85rem;
    opacity: 0.7;

    &--local {
      color: #1976d2;
    }

    &--target {
      color: #7b1fa2;
    }
  }

  .field-name {
    font-weight: 600;
    font-size: 0.9rem;

    &--local {
      color: #1976d2;
    }

    &--target {
      color: #7b1fa2;
    }
  }
}

/* Schema display */
.schema-display {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.schema-type-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.15rem 0.5rem;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;

  &--system {
    background-color: #e8f0fe;
    color: #1a73e8;
  }

  &--custom {
    background-color: #f3e8ff;
    color: #7c3aed;
  }
}

.schema-name {
  font-size: 0.9rem;
  font-weight: 500;
  color: #333;
}

/* Target field readonly (edit mode) */
.target-field-readonly {
  display: inline-block;
  padding: 0.4rem 0.65rem;
  background-color: #f5f5f5;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  color: #666;
  font-size: 0.85rem;
  font-family: 'Courier New', monospace;
}

.target-field-empty {
  color: #ccc;
  font-size: 0.85rem;
}

.target-field-info {
  color: #adb5bd;
  font-size: 0.8rem;
  cursor: help;
  transition: color 0.2s ease;
  margin-left: 2px;

  &:hover {
    color: #666;
  }
}

/* Actions — ALL icons are uniform gray with circular hover background */
.actions-cell {
  display: flex;
  gap: 0.35rem;
  align-items: center;
  justify-content: flex-end;
}

.action-icon {
  cursor: pointer;
  color: #888;
  width: 2.2rem;
  height: 2.2rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  font-size: 0.9rem;
  transition: all 0.2s ease;
  background: transparent;

  &:hover {
    color: #555;
    background-color: #e9ecef;
  }
}

/* Override section — visually nested under its parent relation */
.overrides-inline-section {
  background: #fafafa;

  .overrides-inline-header {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    background: linear-gradient(135deg, #fff8e1 0%, #fffde7 100%);
    border-top: 1px solid #f0e6c0;
    border-bottom: 1px solid #f0e6c0;
    font-size: 0.75rem;
    font-weight: 600;
    color: #b8860b;
    text-transform: uppercase;
    letter-spacing: 0.04em;

    i {
      font-size: 0.8rem;
      color: #f59e0b;
    }
  }
}

/* Add button — same as SchemaFieldsEditor */
.add-relation-container {
  margin-top: 1rem;
  display: flex;
}

.add-relation-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.6rem;
  width: 100%;
  padding: 0.85rem 1.5rem;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.25s ease;
  font-weight: 500;
  border: 1px dashed #ccc;
  color: #666;
  background-color: transparent;

  &:hover {
    border-color: #94c840;
    color: #94c840;
    background-color: #f0f8e8;

    .add-relation-icon-circle {
      background-color: #94c840;
      color: #fff;
    }
  }
}

.add-relation-icon-circle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #e0e0e0;
  color: #666;
  font-size: 0.7rem;
  transition: all 0.25s ease;
}

/* Utilities */
.w-full {
  width: 100%;
}

.ml-tooltip {
  margin-left: 6px;
}
</style>
